/**
 * Aktif Oturumlar kartı (admin/mudur/baskan).
 *
 * Sistemdeki tüm aktif oturumları (refresh token kayıtları) listeler.
 * Her satırda: kullanıcı, cihaz, IP, giriş zamanı, son aktivite, süre.
 * Tek tıkla revoke (zorla logout) ve bir kullanıcının tüm oturumlarını revoke etme.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, RefreshCw, Users, LogOut } from "lucide-react";
import { BACKEND_URL } from "@/config";
import { buildApiCandidates, shouldFailoverBackendRequest } from "@/backendBases";

const API = BACKEND_URL;
const POLL_INTERVAL_MS = 30 * 1000;

async function _getWithFailover(path) {
  const candidates = buildApiCandidates(API);
  let lastErr = null;
  for (const base of candidates) {
    const url = base ? `${base}${path}` : path;
    try {
      const res = await axios.get(url, { withCredentials: true, timeout: 15000 });
      return res;
    } catch (err) {
      lastErr = err;
      if (shouldFailoverBackendRequest(err)) continue;
      throw err;
    }
  }
  throw lastErr;
}

async function _deleteWithFailover(path) {
  const candidates = buildApiCandidates(API);
  let lastErr = null;
  for (const base of candidates) {
    const url = base ? `${base}${path}` : path;
    try {
      const res = await axios.delete(url, { withCredentials: true, timeout: 15000 });
      return res;
    } catch (err) {
      lastErr = err;
      if (shouldFailoverBackendRequest(err)) continue;
      throw err;
    }
  }
  throw lastErr;
}

function _formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

function _shortDevice(ua) {
  if (!ua) return "—";
  // Kısa ve okunaklı bir tahminî açıklama (mobil/desktop + tarayıcı)
  const lower = ua.toLowerCase();
  let device = "Masaüstü";
  if (lower.includes("mobile")) device = "Mobil";
  else if (lower.includes("tablet") || lower.includes("ipad")) device = "Tablet";
  let browser = "Tarayıcı";
  if (lower.includes("edg/")) browser = "Edge";
  else if (lower.includes("chrome/") && !lower.includes("edg/")) browser = "Chrome";
  else if (lower.includes("firefox/")) browser = "Firefox";
  else if (lower.includes("safari/") && !lower.includes("chrome/")) browser = "Safari";
  return `${device} • ${browser}`;
}

export default function ActiveSessionsCard() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null); // {type, sessionId, personnelId, label}

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await _getWithFailover("/api/admin/sessions?only_active=true");
      const list = Array.isArray(res?.data?.sessions) ? res.data.sessions : [];
      setSessions(list);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error("Aktif oturumları görüntüleme yetkiniz yok.");
      } else {
        toast.error("Aktif oturumlar alınamadı.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    const id = setInterval(fetchSessions, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchSessions]);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const s of sessions) {
      const k = s.personnel_id || "?";
      if (!m.has(k)) m.set(k, { count: 0, name: s.personnel_name });
      m.get(k).count += 1;
    }
    return m;
  }, [sessions]);

  const doRevokeOne = async (sessionId, label) => {
    setRevoking(sessionId);
    try {
      await _deleteWithFailover(`/api/admin/sessions/${encodeURIComponent(sessionId)}`);
      toast.success(`${label || "Oturum"} sonlandırıldı.`);
      await fetchSessions();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Oturum sonlandırılamadı.");
    } finally {
      setRevoking(null);
    }
  };

  const doRevokeAll = async (personnelId, label) => {
    setRevoking(`all:${personnelId}`);
    try {
      const res = await _deleteWithFailover(
        `/api/admin/sessions?personnel_id=${encodeURIComponent(personnelId)}`
      );
      const n = res?.data?.revoked_count ?? 0;
      toast.success(`${label || "Kullanıcının"} ${n} oturumu sonlandırıldı.`);
      await fetchSessions();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Oturumlar sonlandırılamadı.");
    } finally {
      setRevoking(null);
    }
  };

  const handleConfirm = async () => {
    if (!confirmRevoke) return;
    if (confirmRevoke.type === "single") {
      await doRevokeOne(confirmRevoke.sessionId, confirmRevoke.label);
    } else if (confirmRevoke.type === "all") {
      await doRevokeAll(confirmRevoke.personnelId, confirmRevoke.label);
    }
    setConfirmRevoke(null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-green-700" />
          Aktif Oturumlar
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Sistemde şu an açık tüm cihaz oturumları. Şüpheli bir oturum varsa "Sonlandır" ile zorla
          çıkış yaptırabilirsiniz. Liste 30 saniyede bir otomatik yenilenir.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-3">
          <Button type="button" variant="outline" onClick={fetchSessions} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-2">Yenile</span>
          </Button>
          <span className="text-xs text-muted-foreground">
            Toplam <strong>{sessions.length}</strong> aktif oturum, <strong>{grouped.size}</strong> kullanıcı.
          </span>
        </div>
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kullanıcı</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Cihaz</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>Giriş</TableHead>
                <TableHead>Son Aktivite</TableHead>
                <TableHead>Süresi Dolacak</TableHead>
                <TableHead className="text-right">İşlem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-6">
                    {loading ? "Yükleniyor..." : "Aktif oturum bulunmuyor."}
                  </TableCell>
                </TableRow>
              ) : (
                sessions.map((s) => {
                  const sId = s.id;
                  const pId = s.personnel_id;
                  const userLabel = s.personnel_name || pId || "—";
                  const isCurrent = Boolean(s.is_current);
                  const groupCount = grouped.get(pId)?.count || 0;
                  const busy = revoking === sId || revoking === `all:${pId}`;
                  return (
                    <TableRow key={sId} className={isCurrent ? "bg-green-50" : undefined}>
                      <TableCell className="font-medium">
                        <div className="flex flex-col">
                          <span>{userLabel}</span>
                          {isCurrent && (
                            <Badge variant="outline" className="mt-1 w-fit text-[10px]">Bu oturum</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{s.role || "—"}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">{_shortDevice(s.user_agent)}</TableCell>
                      <TableCell className="text-xs font-mono">{s.ip_address || "—"}</TableCell>
                      <TableCell className="text-xs">{_formatDateTime(s.created_at)}</TableCell>
                      <TableCell className="text-xs">{_formatDateTime(s.last_used_at)}</TableCell>
                      <TableCell className="text-xs">{_formatDateTime(s.expires_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={isCurrent || busy}
                            onClick={() =>
                              setConfirmRevoke({
                                type: "single",
                                sessionId: sId,
                                label: userLabel,
                              })
                            }
                            data-testid={`revoke-session-${sId}`}
                            title={isCurrent ? "Kendi oturumunuzu buradan sonlandıramazsınız." : "Bu oturumu sonlandır"}
                          >
                            {revoking === sId ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <LogOut className="h-3 w-3" />
                            )}
                            <span className="ml-1 text-xs">Sonlandır</span>
                          </Button>
                          {groupCount > 1 && (
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              disabled={busy}
                              onClick={() =>
                                setConfirmRevoke({
                                  type: "all",
                                  personnelId: pId,
                                  label: userLabel,
                                })
                              }
                              data-testid={`revoke-all-${pId}`}
                              title="Bu kullanıcının tüm aktif oturumlarını sonlandır"
                            >
                              <span className="text-xs">Tümünü</span>
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <AlertDialog open={Boolean(confirmRevoke)} onOpenChange={(o) => !o && setConfirmRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Oturum sonlandırılsın mı?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmRevoke?.type === "all" ? (
                <>
                  <strong>{confirmRevoke?.label}</strong> kullanıcısının <strong>tüm aktif oturumları</strong>{" "}
                  sonlandırılacak. Açık tüm sekmelerde tekrar giriş yapması istenecek.
                </>
              ) : (
                <>
                  <strong>{confirmRevoke?.label}</strong> kullanıcısının seçili oturumu sonlandırılacak. O cihazda
                  tekrar giriş yapması istenecek.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>Sonlandır</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
